import posthog from 'posthog-js';

// PostHog configuration
export const initPostHog = () => {
  if (typeof window !== 'undefined') {
    posthog.init('YOUR_POSTHOG_KEY_HERE', { // Replace with actual PostHog key
      api_host: 'https://app.posthog.com',
      autocapture: false, // We'll manually track business events
      capture_pageview: true,
      capture_pageleave: true,
      loaded: (posthog) => {
        if (import.meta.env.DEV) {
          console.log('PostHog loaded in development mode');
        }
      }
    });
  }
};

// Business event tracking
interface BusinessEvent {
  event: string;
  properties?: Record<string, any>;
  userId?: string;
}

export const trackBusinessEvent = ({ event, properties = {}, userId }: BusinessEvent) => {
  if (typeof window === 'undefined') return;
  
  // Add common properties
  const enhancedProperties = {
    ...properties,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    user_agent: navigator.userAgent,
    environment: import.meta.env.MODE
  };
  
  // Track in PostHog
  posthog.capture(event, enhancedProperties);
  
  // Also send to custom analytics if needed
  console.log('Business Event:', event, enhancedProperties);
};

// Common business events
export const analytics = {
  // User actions
  userSignIn: (userId: string, method: string) => 
    trackBusinessEvent({
      event: 'user_sign_in',
      properties: { method },
      userId
    }),
    
  userSignOut: (userId: string) => 
    trackBusinessEvent({
      event: 'user_sign_out',
      userId
    }),

  // Client management
  clientCreated: (clientId: string, userId: string) =>
    trackBusinessEvent({
      event: 'client_created',
      properties: { client_id: clientId },
      userId
    }),
    
  clientUpdated: (clientId: string, userId: string, changes: string[]) =>
    trackBusinessEvent({
      event: 'client_updated',
      properties: { client_id: clientId, fields_changed: changes },
      userId
    }),

  // Proposal management
  proposalCreated: (proposalId: string, clientId: string, value: number, userId: string) =>
    trackBusinessEvent({
      event: 'proposal_created',
      properties: { 
        proposal_id: proposalId,
        client_id: clientId,
        value: value
      },
      userId
    }),
    
  proposalStatusChanged: (proposalId: string, fromStatus: string, toStatus: string, userId: string) =>
    trackBusinessEvent({
      event: 'proposal_status_changed',
      properties: {
        proposal_id: proposalId,
        from_status: fromStatus,
        to_status: toStatus
      },
      userId
    }),

  // Invoice management
  invoiceCreated: (invoiceId: string, clientId: string, amount: number, userId: string) =>
    trackBusinessEvent({
      event: 'invoice_created',
      properties: {
        invoice_id: invoiceId,
        client_id: clientId,
        amount: amount
      },
      userId
    }),
    
  invoiceStatusChanged: (invoiceId: string, fromStatus: string, toStatus: string, userId: string) =>
    trackBusinessEvent({
      event: 'invoice_status_changed',
      properties: {
        invoice_id: invoiceId,
        from_status: fromStatus,
        to_status: toStatus
      },
      userId
    }),
    
  invoicePaid: (invoiceId: string, amount: number, paymentMethod: string, userId: string) =>
    trackBusinessEvent({
      event: 'invoice_paid',
      properties: {
        invoice_id: invoiceId,
        amount: amount,
        payment_method: paymentMethod
      },
      userId
    }),

  // Project management
  projectCreated: (projectId: string, clientId: string, value: number, userId: string) =>
    trackBusinessEvent({
      event: 'project_created',
      properties: {
        project_id: projectId,
        client_id: clientId,
        value: value
      },
      userId
    }),
    
  projectStatusChanged: (projectId: string, fromStatus: string, toStatus: string, userId: string) =>
    trackBusinessEvent({
      event: 'project_status_changed',
      properties: {
        project_id: projectId,
        from_status: fromStatus,
        to_status: toStatus
      },
      userId
    }),

  // Error tracking
  errorOccurred: (error: string, context: Record<string, any>, userId?: string) =>
    trackBusinessEvent({
      event: 'error_occurred',
      properties: {
        error_message: error,
        context: context
      },
      userId
    }),

  // Feature usage
  featureUsed: (feature: string, userId: string, metadata?: Record<string, any>) =>
    trackBusinessEvent({
      event: 'feature_used',
      properties: {
        feature_name: feature,
        ...metadata
      },
      userId
    })
};

// User identification
export const identifyUser = (userId: string, traits: Record<string, any>) => {
  if (typeof window === 'undefined') return;
  
  posthog.identify(userId, traits);
};

export const resetUser = () => {
  if (typeof window === 'undefined') return;
  
  posthog.reset();
};